import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import ResourceCard from '../components/ResourceCard';
import ResourceModal from '../components/ResourceModal';
import ConfirmDialog from '../components/ConfirmDialog';

const ResourcesManagementPage = () => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editingResource, setEditingResource] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, resourceId: null });

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedLanguage, setSelectedLanguage] = useState('All');

    const fetchResources = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/resources');
            if (response.data.success) {
                setResources(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching resources:', error);
            toast.error('Failed to load resources');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, []);

    // Filter resources
    const filteredResources = resources.filter(res => {
        const matchesSearch = res.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                             res.category.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || res.category === selectedCategory;
        const matchesLanguage = selectedLanguage === 'All' || 
                               (selectedLanguage === 'None' ? !res.language : res.language === selectedLanguage);
        
        return matchesSearch && matchesCategory && matchesLanguage;
    });

    const handleAddNew = () => {
        setEditingResource(null);
        setModalOpen(true);
    };

    const handleEdit = (resource) => {
        setEditingResource(resource);
        setModalOpen(true);
    };

    const handleDelete = (resourceId) => {
        setDeleteConfirm({ isOpen: true, resourceId });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/resources/${deleteConfirm.resourceId}`);
            toast.success('Resource deleted successfully');
            setDeleteConfirm({ isOpen: false, resourceId: null });
            fetchResources();
        } catch (error) {
            console.error('Error deleting resource:', error);
            toast.error('Failed to delete resource');
        }
    };

    const handleSave = async (formData) => {
        try {
            setSubmitting(true);
            if (editingResource) {
                // Update
                await axios.put(`/api/admin/resources/${editingResource.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Resource updated successfully');
            } else {
                // Create
                await axios.post('/api/admin/resources', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Resource uploaded successfully');
            }
            setModalOpen(false);
            setEditingResource(null);
            fetchResources();
        } catch (error) {
            console.error('Error saving resource:', error);
            toast.error(error.response?.data?.message || 'Failed to save resource');
        } finally {
            setSubmitting(false);
        }
    };

    // Extract categories for dynamic filters
    const categories = ['All', ...new Set(resources.map(r => r.category))];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Resource Documents Management</h1>
                    <p className="text-slate-500 mt-1">Upload and manage dynamic product brochures and customer checklists</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-[#283079] hover:bg-[#202660] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl shrink-0"
                >
                    <PlusIcon className="h-5 w-5" />
                    Upload New Document
                </button>
            </div>

            {/* Filter Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col md:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="relative w-full md:max-w-md">
                    <MagnifyingGlassIcon className="h-5 w-5 text-slate-400 absolute left-4 top-3.5" />
                    <input
                        type="text"
                        placeholder="Search by title or category..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all text-sm text-slate-700"
                    />
                </div>

                {/* Filters Group */}
                <div className="flex flex-wrap gap-3 w-full md:w-auto items-center justify-end">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
                        <FunnelIcon className="h-4 w-4" />
                        Filters:
                    </div>

                    {/* Category Filter */}
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="px-4 py-2 border border-slate-200 rounded-xl bg-white text-sm font-semibold text-slate-600 outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat === 'All' ? 'All Categories' : cat}</option>
                        ))}
                    </select>

                    {/* Language Filter */}
                    <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="px-4 py-2 border border-slate-200 rounded-xl bg-white text-sm font-semibold text-slate-600 outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                        <option value="All">All Languages</option>
                        <option value="Telugu">Telugu</option>
                        <option value="Tamil">Tamil</option>
                        <option value="Kannada">Kannada</option>
                        <option value="English">English</option>
                        <option value="None">None / General</option>
                    </select>
                </div>
            </div>

            {/* List Section */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-pulse space-y-4">
                            <div className="flex justify-between items-center">
                                <div className="h-5 w-24 bg-slate-200 rounded-full"></div>
                                <div className="h-5 w-16 bg-slate-200 rounded-full"></div>
                            </div>
                            <div className="h-6 bg-slate-200 rounded w-3/4"></div>
                            <div className="h-10 bg-slate-100 rounded-xl"></div>
                            <div className="flex justify-end gap-2">
                                <div className="h-8 w-8 bg-slate-200 rounded-lg"></div>
                                <div className="h-8 w-8 bg-slate-200 rounded-lg"></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : filteredResources.length === 0 ? (
                <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto space-y-4">
                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-2 text-slate-400">
                            <FunnelIcon className="h-8 w-8" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800">No documents found</h3>
                        <p className="text-slate-500">
                            There are no documents matching your search queries or filter selections.
                        </p>
                        {resources.length === 0 && (
                            <button
                                onClick={handleAddNew}
                                className="bg-[#283079] hover:bg-[#202660] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg"
                            >
                                Upload First Document
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredResources.map(res => (
                        <ResourceCard
                            key={res.id}
                            resource={res}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Upload/Edit Modal */}
            {modalOpen && (
                <ResourceModal
                    isOpen={modalOpen}
                    onClose={() => { setModalOpen(false); setEditingResource(null); }}
                    onSave={handleSave}
                    resource={editingResource}
                    loading={submitting}
                />
            )}

            {/* Delete Confirm */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, resourceId: null })}
                onConfirm={confirmDelete}
                title="Delete Resource Document"
                message="Are you sure you want to permanently delete this resource document? This will remove the file from the server."
            />
        </div>
    );
};

export default ResourcesManagementPage;
